import type {
  Theme,
  ThemeComponents,
  ThemeMarkdown,
  ThemeMode,
  ThemePalette,
  ThemePreference,
  ThemeSemantic,
  ThemeSyntax,
} from "./termui/base";

export type ThemeModeTokens = {
  palette: ThemePalette;
  semantic: ThemeSemantic;
  syntax: ThemeSyntax;
  markdown: ThemeMarkdown;
  components: ThemeComponents;
};

export type ThemeSpec = {
  id: string;
  label: string;
  version: number;
  modes: Record<ThemeMode, ThemeModeTokens>;
};

export type ThemeValidationIssue = {
  path: string;
  message: string;
};

export type ThemeValidationResult = {
  ok: boolean;
  issues: ThemeValidationIssue[];
  theme?: ThemeSpec;
};

const DARK_PALETTE: ThemePalette = {
  black: "#0c0c0c",
  red: "#e06c75",
  green: "#98c379",
  yellow: "#e5c07b",
  blue: "#61afef",
  magenta: "#c678dd",
  cyan: "#56b6c2",
  white: "#cccccc",
  brightBlack: "#555555",
  brightWhite: "#ffffff",
};

const DARK_SEMANTIC: ThemeSemantic = {
  frameBg: "#0c0c0c",
  frameFg: "#cccccc",
  frameMuted: "#555555",
  border: "#555555",
  borderStrong: "#808080",
  inverseFg: "#0c0c0c",
  inverseBg: "#ffffff",
  info: "#56b6c2",
  success: "#98c379",
  warn: "#e5c07b",
  danger: "#e06c75",
};

const DARK_SYNTAX: ThemeSyntax = {
  default: "#ffffff",
  keyword: "#c678dd",
  string: "#98c379",
  number: "#e5c07b",
  comment: "#555555",
  punctuation: "#555555",
  function: "#61afef",
  type: "#56b6c2",
};

const DARK_MARKDOWN: ThemeMarkdown = {
  headingMarker: "#555555",
  headingBoldMaxLevel: 3,
  headings: {
    h1: "#e5c07b",
    h2: "#56b6c2",
    h3: "#98c379",
    h4: "#c678dd",
    h5: "#cccccc",
    h6: "#cccccc",
  },
  paragraph: "#cccccc",
  unorderedBullet: "#56b6c2",
  orderedMarker: "#56b6c2",
  tableHeader: "#56b6c2",
  tableBorder: "#555555",
  quoteMarker: "#555555",
  quoteText: "#555555",
  separator: "#555555",
  codeText: "#ffffff",
  codeEmpty: "#555555",
  codeBorder: "#555555",
};

const DARK_COMPONENTS: ThemeComponents = {
  tabs: {
    activeFg: "#0c0c0c",
    activeBg: "#e5c07b",
    inactiveFg: "#555555",
    separator: "#555555",
  },
  statusbar: {
    fg: "#0c0c0c",
    bg: "#98c379",
  },
  badge: {
    fg: "#0c0c0c",
    bg: "#ffffff",
  },
  button: {
    fg: "#0c0c0c",
    bg: "#ffffff",
  },
};

const LIGHT_PALETTE: ThemePalette = {
  black: "#1f2328",
  red: "#cf222e",
  green: "#1a7f37",
  yellow: "#9a6700",
  blue: "#0969da",
  magenta: "#8250df",
  cyan: "#1b7c83",
  white: "#30363d",
  brightBlack: "#6e7781",
  brightWhite: "#0f172a",
};

const LIGHT_SEMANTIC: ThemeSemantic = {
  frameBg: "#f6f8fa",
  frameFg: "#1f2328",
  frameMuted: "#6e7781",
  border: "#d0d7de",
  borderStrong: "#8c959f",
  inverseFg: "#f6f8fa",
  inverseBg: "#0f172a",
  info: "#0969da",
  success: "#1a7f37",
  warn: "#9a6700",
  danger: "#cf222e",
};

const LIGHT_SYNTAX: ThemeSyntax = {
  default: "#1f2328",
  keyword: "#8250df",
  string: "#116329",
  number: "#9a6700",
  comment: "#6e7781",
  punctuation: "#6e7781",
  function: "#0969da",
  type: "#1b7c83",
};

const LIGHT_MARKDOWN: ThemeMarkdown = {
  headingMarker: "#6e7781",
  headingBoldMaxLevel: 3,
  headings: {
    h1: "#9a6700",
    h2: "#0969da",
    h3: "#1a7f37",
    h4: "#8250df",
    h5: "#1f2328",
    h6: "#1f2328",
  },
  paragraph: "#1f2328",
  unorderedBullet: "#0969da",
  orderedMarker: "#0969da",
  tableHeader: "#0969da",
  tableBorder: "#d0d7de",
  quoteMarker: "#6e7781",
  quoteText: "#6e7781",
  separator: "#d0d7de",
  codeText: "#1f2328",
  codeEmpty: "#6e7781",
  codeBorder: "#d0d7de",
};

const LIGHT_COMPONENTS: ThemeComponents = {
  tabs: {
    activeFg: "#f6f8fa",
    activeBg: "#0969da",
    inactiveFg: "#6e7781",
    separator: "#8c959f",
  },
  statusbar: {
    fg: "#f6f8fa",
    bg: "#0969da",
  },
  badge: {
    fg: "#f6f8fa",
    bg: "#0f172a",
  },
  button: {
    fg: "#f6f8fa",
    bg: "#0f172a",
  },
};

export const defaultThemeSpec: ThemeSpec = {
  id: "termui-default",
  label: "TermUI Default",
  version: 1,
  modes: {
    dark: {
      palette: DARK_PALETTE,
      semantic: DARK_SEMANTIC,
      syntax: DARK_SYNTAX,
      markdown: DARK_MARKDOWN,
      components: DARK_COMPONENTS,
    },
    light: {
      palette: LIGHT_PALETTE,
      semantic: LIGHT_SEMANTIC,
      syntax: LIGHT_SYNTAX,
      markdown: LIGHT_MARKDOWN,
      components: LIGHT_COMPONENTS,
    },
  },
};

export const builtinThemes = {
  default: defaultThemeSpec,
} as const;

export function resolveThemeMode(
  preference: ThemePreference,
  systemMode: ThemeMode = "dark",
): ThemeMode {
  return preference === "system" ? systemMode : preference;
}

export function resolveTheme(
  spec: ThemeSpec,
  preference: ThemePreference = "dark",
  opts?: { systemMode?: ThemeMode },
): Theme {
  const mode = resolveThemeMode(preference, opts?.systemMode ?? "dark");
  const modeTokens = spec.modes[mode] ?? defaultThemeSpec.modes[mode];

  return {
    id: spec.id,
    label: spec.label,
    version: spec.version,
    mode,
    palette: modeTokens.palette,
    semantic: modeTokens.semantic,
    syntax: modeTokens.syntax,
    markdown: modeTokens.markdown,
    components: modeTokens.components,
    black: modeTokens.palette.black,
    red: modeTokens.palette.red,
    green: modeTokens.palette.green,
    yellow: modeTokens.palette.yellow,
    blue: modeTokens.palette.blue,
    magenta: modeTokens.palette.magenta,
    cyan: modeTokens.palette.cyan,
    white: modeTokens.palette.white,
    brightBlack: modeTokens.palette.brightBlack,
    brightWhite: modeTokens.palette.brightWhite,
    border: modeTokens.semantic.border,
    bg: modeTokens.semantic.frameBg,
  };
}

export const dark = resolveTheme(defaultThemeSpec, "dark");
export const light = resolveTheme(defaultThemeSpec, "light");

export function themeToCssVars(theme: Theme): Record<string, string> {
  return {
    "--termui-bg": theme.semantic.frameBg,
    "--termui-fg": theme.semantic.frameFg,
    "--termui-muted": theme.semantic.frameMuted,
    "--termui-border": theme.semantic.border,
    "--termui-border-strong": theme.semantic.borderStrong,
    "--termui-info": theme.semantic.info,
    "--termui-success": theme.semantic.success,
    "--termui-warn": theme.semantic.warn,
    "--termui-danger": theme.semantic.danger,
    "--termui-inverse-fg": theme.semantic.inverseFg,
    "--termui-inverse-bg": theme.semantic.inverseBg,
  };
}

export function parseThemeSpec(json: string): ThemeValidationResult {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch (error) {
    return {
      ok: false,
      issues: [{ path: "$", message: error instanceof Error ? error.message : "Invalid JSON" }],
    };
  }
  return validateThemeSpec(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function issue(path: string, message: string): ThemeValidationIssue {
  return { path, message };
}

function isColorValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (/^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v)) return true;
  if (/^(?:rgb|rgba|hsl|hsla|oklch|oklab|lab|lch|color)\(.+\)$/i.test(v)) return true;
  if (/^var\(--[a-zA-Z0-9-_]+\)$/.test(v)) return true;
  if (/^[a-zA-Z]+$/.test(v)) return true;
  return false;
}

function validateColorField(
  value: unknown,
  path: string,
  issues: ThemeValidationIssue[],
) {
  if (typeof value !== "string") {
    issues.push(issue(path, "Expected a string color value"));
    return;
  }
  if (!isColorValue(value)) {
    issues.push(issue(path, "Invalid CSS color value"));
  }
}

function validateHeadingBoldMaxLevel(
  value: unknown,
  path: string,
  issues: ThemeValidationIssue[],
) {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    issues.push(issue(path, "Expected an integer between 1 and 6"));
    return;
  }
  if (value < 1 || value > 6) {
    issues.push(issue(path, "Expected an integer between 1 and 6"));
  }
}

function validateTokenGroup(
  value: unknown,
  path: string,
  fields: readonly string[],
  issues: ThemeValidationIssue[],
) {
  if (!isRecord(value)) {
    issues.push(issue(path, "Expected an object"));
    return;
  }
  for (const field of fields) {
    validateColorField(value[field], `${path}.${field}`, issues);
  }
}

function validateMarkdownTokens(
  value: unknown,
  path: string,
  issues: ThemeValidationIssue[],
) {
  if (!isRecord(value)) {
    issues.push(issue(path, "Expected an object"));
    return;
  }

  validateColorField(value.headingMarker, `${path}.headingMarker`, issues);
  validateHeadingBoldMaxLevel(value.headingBoldMaxLevel, `${path}.headingBoldMaxLevel`, issues);

  if (!isRecord(value.headings)) {
    issues.push(issue(`${path}.headings`, "Expected an object"));
  } else {
    for (const level of ["h1", "h2", "h3", "h4", "h5", "h6"] as const) {
      validateColorField(value.headings[level], `${path}.headings.${level}`, issues);
    }
  }

  for (const field of [
    "paragraph",
    "unorderedBullet",
    "orderedMarker",
    "tableHeader",
    "tableBorder",
    "quoteMarker",
    "quoteText",
    "separator",
    "codeText",
    "codeEmpty",
    "codeBorder",
  ] as const) {
    validateColorField(value[field], `${path}.${field}`, issues);
  }
}

function validateComponentTokens(
  value: unknown,
  path: string,
  issues: ThemeValidationIssue[],
) {
  if (!isRecord(value)) {
    issues.push(issue(path, "Expected an object"));
    return;
  }

  validateTokenGroup(
    value.tabs,
    `${path}.tabs`,
    ["activeFg", "activeBg", "inactiveFg", "separator"],
    issues,
  );
  validateTokenGroup(value.statusbar, `${path}.statusbar`, ["fg", "bg"], issues);
  validateTokenGroup(value.badge, `${path}.badge`, ["fg", "bg"], issues);
  validateTokenGroup(value.button, `${path}.button`, ["fg", "bg"], issues);
}

function validateModeTokens(
  value: unknown,
  path: string,
  issues: ThemeValidationIssue[],
) {
  if (!isRecord(value)) {
    issues.push(issue(path, "Expected an object"));
    return;
  }

  validateTokenGroup(
    value.palette,
    `${path}.palette`,
    [
      "black",
      "red",
      "green",
      "yellow",
      "blue",
      "magenta",
      "cyan",
      "white",
      "brightBlack",
      "brightWhite",
    ],
    issues,
  );

  validateTokenGroup(
    value.semantic,
    `${path}.semantic`,
    [
      "frameBg",
      "frameFg",
      "frameMuted",
      "border",
      "borderStrong",
      "inverseFg",
      "inverseBg",
      "info",
      "success",
      "warn",
      "danger",
    ],
    issues,
  );

  validateTokenGroup(
    value.syntax,
    `${path}.syntax`,
    [
      "default",
      "keyword",
      "string",
      "number",
      "comment",
      "punctuation",
      "function",
      "type",
    ],
    issues,
  );

  validateMarkdownTokens(value.markdown, `${path}.markdown`, issues);
  validateComponentTokens(value.components, `${path}.components`, issues);
}

export function validateThemeSpec(value: unknown): ThemeValidationResult {
  const issues: ThemeValidationIssue[] = [];
  if (!isRecord(value)) {
    return { ok: false, issues: [issue("$", "Expected an object")] };
  }

  if (typeof value.id !== "string" || !value.id.trim()) {
    issues.push(issue("$.id", "Expected a non-empty string"));
  }

  if (typeof value.label !== "string" || !value.label.trim()) {
    issues.push(issue("$.label", "Expected a non-empty string"));
  }

  if (typeof value.version !== "number" || !Number.isInteger(value.version) || value.version < 1) {
    issues.push(issue("$.version", "Expected a positive integer"));
  }

  if (!isRecord(value.modes)) {
    issues.push(issue("$.modes", "Expected an object with light/dark modes"));
  } else {
    validateModeTokens(value.modes.dark, "$.modes.dark", issues);
    validateModeTokens(value.modes.light, "$.modes.light", issues);
  }

  if (issues.length > 0) {
    return { ok: false, issues };
  }

  return {
    ok: true,
    issues: [],
    theme: value as ThemeSpec,
  };
}
